import React from "react";
import { motion, AnimatePresence } from "motion/react";
import { Handshake, ShieldCheck, Zap, ArrowRight, X } from "lucide-react";

interface CNPLComingSoonModalProps {
  isOpen: boolean;
  onClose: () => void;
}

export function CNPLComingSoonModal({ isOpen, onClose }: CNPLComingSoonModalProps) {
  return (
    <AnimatePresence>
      {isOpen && (
        <div className="fixed inset-0 z-[100] flex items-center justify-center p-4">
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={onClose}
            className="absolute inset-0 bg-[#0D1A12]/80 backdrop-blur-md"
          />
          <motion.div
            initial={{ opacity: 0, scale: 0.9, y: 20 }}
            animate={{ opacity: 1, scale: 1, y: 0 }}
            exit={{ opacity: 0, scale: 0.9, y: 20 }}
            className="relative w-full max-w-xl bg-white rounded-[2.5rem] shadow-2xl overflow-hidden"
          >
            <div className="absolute top-0 right-0 w-64 h-64 bg-[#0B6E4F]/5 rounded-full blur-3xl -mr-32 -mt-32" />
            
            <button 
              onClick={onClose}
              className="absolute top-6 right-6 p-2 bg-gray-50 hover:bg-gray-100 rounded-xl transition-colors z-20 text-gray-400"
            >
              <X className="w-5 h-5" />
            </button>

            <div className="relative z-10 p-8 md:p-12">
              <div className="flex flex-col items-center text-center mb-10">
                <div className="w-20 h-20 bg-emerald-50 rounded-3xl flex items-center justify-center text-[#0B6E4F] mb-6 shadow-sm">
                  <Handshake className="w-10 h-10" />
                </div>
                <h2 className="text-3xl font-black text-[#0D1A12] tracking-tight mb-3">
                  Inventory Credit (CNPL)
                </h2>
                <div className="flex items-center gap-2 px-4 py-1.5 bg-amber-50 rounded-full border border-amber-100">
                  <span className="text-[10px] font-black text-amber-700 uppercase tracking-widest leading-none">LAUNCHING SOON</span>
                </div>
              </div>

              <div className="space-y-6">
                {[
                  {
                    icon: Zap,
                    title: "Procure Without Capital",
                    desc: "Source inventory from other verified MoniFlow sellers with 0% upfront cost. Pay back only once you make sales."
                  },
                  {
                    icon: ShieldCheck,
                    title: "Build Your Credit Score",
                    desc: "Every timely settlement increases your peer credit score, unlocking higher credit limits and exclusive wholesale deals."
                  },
                  {
                    icon: Handshake,
                    title: "Seamless Settlements",
                    desc: "Say goodbye to manual tracking. Obligations are automatically settled as you log sales through the MoniFlow app."
                  }
                ].map((feature, idx) => (
                  <motion.div 
                    initial={{ opacity: 0, x: -20 }}
                    animate={{ opacity: 1, x: 0 }}
                    transition={{ delay: 0.2 + (idx * 0.1) }}
                    key={idx} 
                    className="flex gap-4 p-4 rounded-2xl border border-gray-100 bg-gray-50/50 hover:bg-white hover:shadow-lg hover:shadow-emerald-900/5 transition-all group"
                  >
                    <div className="w-12 h-12 rounded-xl bg-white shadow-sm flex items-center justify-center text-[#0B6E4F] group-hover:scale-110 transition-transform flex-shrink-0">
                      <feature.icon className="w-6 h-6" />
                    </div>
                    <div>
                      <h4 className="font-bold text-[#0D1A12] text-sm group-hover:text-[#0B6E4F] transition-colors">{feature.title}</h4>
                      <p className="text-xs text-[#6B7280] leading-relaxed mt-0.5">{feature.desc}</p>
                    </div>
                  </motion.div>
                ))}
              </div>

              <div className="mt-10 pt-8 border-t border-gray-100">
                <button
                  onClick={onClose}
                  className="w-full py-5 rounded-2xl bg-[#0B6E4F] text-white font-black text-sm uppercase tracking-[0.15em] shadow-xl shadow-[#0B6E4F]/20 hover:scale-[1.02] active:scale-[0.98] transition-all flex items-center justify-center gap-2"
                >
                  I'm Interested <ArrowRight className="w-4 h-4" />
                </button>
              </div>
            </div>
          </motion.div>
        </div>
      )}
    </AnimatePresence>
  );
}
